import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { resetReservationTimer } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { isNullOrUndefined } from 'Util/TypeGuards';
import alertToast from 'Util/ToastifyUtils';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import {
	clearOldWizardData,
	getDefaultWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { trackAddAnotherBooking } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import { analyticsBookingType, analyticsRouteLabel } from 'Util/_HumanWritten/Analytics/analyticsBookingFields';

export interface AddAnotherBookingButtonProps {
	wizardData?: BookingWizardData;
	onUpdateWizardData: (wizardData: BookingWizardData) => void;
	redirectURL: string;
	className?: string;
}

export function AddAnotherBookingButton({
	wizardData,
	onUpdateWizardData,
	redirectURL,
	className,
}: AddAnotherBookingButtonProps) {
	const navigate = useNavigate();

	const onAddNewBooking = (oldWizardData: BookingWizardData) => {
		trackAddAnotherBooking({
			booking_type: analyticsBookingType(oldWizardData.tabSelected),
			route: analyticsRouteLabel(oldWizardData.fromLocationId, oldWizardData.toLocationId),
		});
		const transactionId = getFerryBookingTransactionIdFromStorage();
		if (transactionId !== null) {
			resetReservationTimer(transactionId).then(() => {
				const newData = getDefaultWizardData({
					tabSelected: oldWizardData.tabSelected,
				});
				newData.tripType = oldWizardData.tripType;
				newData.fromLocationId = oldWizardData.fromLocationId;
				newData.toLocationId = oldWizardData.toLocationId;
				newData.startDate = oldWizardData.startDate;
				newData.endDate = oldWizardData.endDate;
				newData.ticketSelectionStartDate = oldWizardData.ticketSelectionStartDate;
				newData.ticketSelectionEndDate = oldWizardData.ticketSelectionEndDate;
				newData.userId = oldWizardData.userId;
				if (
					newData.tabSelected === 'freight' &&
					newData.innerWizardData.type === 'freight' &&
					oldWizardData.tabSelected === 'freight' &&
					oldWizardData.innerWizardData.type === 'freight'
				) {
					newData.innerWizardData.receiverFirstName = oldWizardData.innerWizardData.receiverFirstName;
					newData.innerWizardData.receiverLastName = oldWizardData.innerWizardData.receiverLastName;
					newData.innerWizardData.receiverPhone = oldWizardData.innerWizardData.receiverPhone;
					newData.innerWizardData.receiverEmail = oldWizardData.innerWizardData.receiverEmail;
					newData.innerWizardData.receiverCompanyName = oldWizardData.innerWizardData.receiverCompanyName;
					newData.innerWizardData.notifyFirstName = oldWizardData.innerWizardData.notifyFirstName;
					newData.innerWizardData.notifyLastName = oldWizardData.innerWizardData.notifyLastName;
					newData.innerWizardData.notifyPhone = oldWizardData.innerWizardData.notifyPhone;
					newData.innerWizardData.notifyEmail = oldWizardData.innerWizardData.notifyEmail;
					newData.innerWizardData.notifyCompanyName = oldWizardData.innerWizardData.notifyCompanyName;
					newData.departureTicketId = oldWizardData.departureTicketId;
					newData.departingJourney = oldWizardData.departingJourney;
					newData.ticketSelectionStartDate = oldWizardData.ticketSelectionStartDate;
				}

				onUpdateWizardData(newData);
				clearOldWizardData();
				navigate(redirectURL);
			})
			// Without this the rejection is unhandled and the button simply appears dead — the
			// extend can be refused (a 403 once the hold has lapsed), and everything above,
			// including the navigation, lives in the `then`.
			.catch(() => {
				alertToast('Could not extend booking timer', 'error');
			});
		}
	};

	if (isNullOrUndefined(wizardData)) {
		return <></>;
	}

	return (
		<Button
			className={`add-another-booking-button ${className ?? ''}`.trim()}
			onClick={() => {
				onAddNewBooking(wizardData);
			}}
			display={Display.Outline}
			colors={Colors.Secondary}
			icon={{ icon: 'add', iconPos: 'icon-left' }}
			newStyles
		>
			Add another booking
		</Button>
	);
}
